<template>
  <div class="vx-row">
    <div class="w-full vx-col">
      <!-- Main Card -->
      <vx-card :title="'SO LEAD TIME'">
        <div id="so-lead-time-table" class="vx-row">
          <div class="w-full vx-col">
            <vs-table stripe class="mt-8" :data="items">
              <template slot="thead">
                <vs-th width="25%">REGION</vs-th>
                <vs-th width="45%">RANGES (DAYS)</vs-th>
                <vs-th width="15%">STATUS</vs-th>
                <vs-th width="15%">ACTION</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr @click.native="onClickUpdate(tr)" class="cursor-pointer" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td :data="tr.region">
                    <span class="font-semibold">{{ tr.region }}</span>
                  </vs-td>

                  <vs-td :data="tr.ranges">
                    <div v-if="tr.ranges && tr.ranges.length > 0" class="flex flex-wrap gap-1">
                      <vs-chip
                        v-for="(range, rIdx) in tr.ranges"
                        :key="rIdx"
                        color="primary"
                        class="mr-2 mb-1"
                      >
                        {{ range.from }} - {{ range.to }} days
                      </vs-chip>
                    </div>
                    <span v-else class="text-grey italic">No lead time ranges configured</span>
                  </vs-td>

                  <vs-td>
                    <vs-chip v-if="tr.ranges && tr.ranges.length > 0" color="success">
                      Configured
                    </vs-chip>
                    <vs-chip v-else color="warning">
                      Not Configured
                    </vs-chip>
                  </vs-td>

                  <vs-td>
                    <div class="justify-start vx-row">
                      <vx-tooltip text="View/Update" position="left">
                        <vs-button
                          @click.stop="onClickUpdate(tr)"
                          class="ml-3"
                          size="small"
                          color="warning"
                          type="border"
                          icon-pack="feather"
                          icon="icon-edit-2"
                        />
                      </vx-tooltip>
                    </div>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </div>
      </vx-card>
    </div>

    <!-- Popup for Adding / Updating Lead Time Ranges -->
    <ManageSoLeadTimePopUp ref="manageSoLeadTimePopUp" />
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ManageSoLeadTimePopUp from '@/components/popups/settings/so-lead-time/ManageSoLeadTimePopUp'
import ErrorMessages from '@/mixins/ErrorMessages'
import SuccessMessage from '@/mixins/SuccessMessage'

const SalesOrderLeadTimeRepository = RepositoryFactory.get('so_lead_time')
const FIXED_REGIONS = ['Great Britain', 'Europe', 'ROW']

export default {
  name: 'SoLeadTimeSettings',
  mixins: [ErrorMessages, SuccessMessage],
  components: {
    ManageSoLeadTimePopUp
  },
  data() {
    return {
      items: [],
      regions: FIXED_REGIONS
    }
  },
  methods: {
    async refreshData() {
      await this.fetchData()
    },
    async fetchData() {
      await this.$vs.loading({
        container: '#so-lead-time-table',
        scale: 0.45
      })
      try {
        // 1. Fetch sales order lead time list
        const leadTimeRes = await SalesOrderLeadTimeRepository.getSoLeadTimeList()
        const rawLeadTimes = (leadTimeRes.data && leadTimeRes.data.data) ? leadTimeRes.data.data : (leadTimeRes.data || [])
        const normalizedLeadTimes = this.normalizeLeadTimes(rawLeadTimes)

        // 2. Initialize map for fixed 3 regions
        const regionMap = {}
        FIXED_REGIONS.forEach(regionName => {
          regionMap[regionName.toLowerCase()] = {
            region: regionName,
            ranges: []
          }
        })

        // 3. Merge configured lead times into fixed regions (matching by name)
        normalizedLeadTimes.forEach(lt => {
          const regionName = lt.region || lt.name || ''
          const ranges = Array.isArray(lt.ranges) ? lt.ranges : []
          ranges.sort((a, b) => Number(a.from) - Number(b.from))
          const key = regionName.toLowerCase()
          if (regionMap[key]) {
            regionMap[key].ranges = ranges
            if (lt.id) regionMap[key].id = lt.id
          } else if (regionName) {
            regionMap[key] = {
              region: regionName,
              ranges: ranges,
              id: lt.id
            }
          }
        })

        this.items = Object.values(regionMap)
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.$vs.loading.close('#so-lead-time-table > .con-vs-loading')
      }
    },
    normalizeLeadTimes(data) {
      if (!data) return []

      if (Array.isArray(data)) {
        const grouped = {}
        data.forEach(item => {
          if (!item) return
          if (Array.isArray(item.ranges)) {
            const reg = item.region || item.name || ''
            const key = reg.toLowerCase()
            if (!grouped[key]) {
              grouped[key] = { region: reg, ranges: [], id: item.id }
            }
            grouped[key].ranges.push(...item.ranges)
          } else if (item.region && item.from !== undefined && item.to !== undefined) {
            const key = item.region.toLowerCase()
            if (!grouped[key]) {
              grouped[key] = { region: item.region, ranges: [] }
            }
            grouped[key].ranges.push({
              id: item.id,
              from: item.from,
              to: item.to
            })
          } else if (item.region) {
            const key = item.region.toLowerCase()
            if (!grouped[key]) {
              grouped[key] = { region: item.region, ranges: item.ranges || [] }
            }
          }
        })
        return Object.values(grouped)
      }

      if (typeof data === 'object') {
        const list = []
        for (const [key, val] of Object.entries(data)) {
          if (Array.isArray(val)) {
            list.push({ region: key, ranges: val })
          } else if (val && typeof val === 'object') {
            list.push({
              region: val.region || key,
              ranges: val.ranges || [],
              id: val.id
            })
          }
        }
        return list
      }

      return []
    },
    onClickUpdate(tr) {
      this.$refs.manageSoLeadTimePopUp.openPopup(tr, this.regions)
    }
  },
  async mounted() {
    await this.fetchData()
  }
}
</script>

<style scoped>
.gap-1 {
  gap: 0.25rem;
}
</style>
